/**
 * Install Banner — PWA Install Prompt UI
 * Spec ref: SPEC-025 §3.5 (Install Banner Component),
 *           §2.2 (30s delay OR 2nd visit, dismiss 7 days)
 */

'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Download, X } from 'lucide-react';

// AUDIT-070-009 (SPEC-070 QA Audit staging 2026-08-25 v6 — CRITICAL):
// Routes where the PWA InstallBanner is suppressed. The banner is
// `position: fixed bottom-4 z-50` and on the auth-flow forms (/login,
// /register, /forgot-password, /admin/login) it covers the primary
// submit button ("Entrar" / "Crear cuenta" / "Enviar enlace") because
// those forms are taller than `viewport - bottom-4` (especially with
// the on-screen keyboard open on mobile). Users couldn't tap the
// submit button — agent-browser detected "Element covered by
// <div.fixed.bottom-4>". The banner is also UX noise on auth pages
// (the user is trying to log in, NOT install the PWA).
//
// Fix Opción A (preferred per SPEC-070 §3): route-level suppression
// rather than body padding (Opción B) — the banner is hidden on these
// 4 routes entirely; it still appears on /app/* and all other authed
// routes. SPEC-025 (PWA spec) is NOT changed — the banner stays
// installable everywhere else; this is a UX gate scoped to onboarding.
//
// §6 Anti-Invención: NO business logic — purely a UI gate.
// Trazabilidad §6: SPEC-070 §AUDIT-070-009.
const SUPPRESSED_ROUTES = new Set<string>([
  '/login',
  '/register',
  '/forgot-password',
  '/admin/login',
]);

/**
 * Pendiente #13 (snapshot Sprint 130 — fix 2026-09-16): route prefixes
 * where the banner is suppressed because its `fixed top-4` mobile position
 * covers critical page-level buttons (e.g., the page header "Salir" button
 * in /sala/[slug] which is the user's only way to leave a conference room
 * they joined as a guest — pre-fix, the PWA banner with `z-50` covered it
 * and the click landed on the banner instead of triggering handleLeave).
 *
 * We use `.startsWith()` for these (vs the exact-match Set above) because
 * these are dynamic route prefixes (`/sala/[slug]`).
 *
 * §6 Anti-Invención: pure UI gate — same pattern as `SUPPRESSED_ROUTES`
 * for /login + /register. No business logic.
 */
const SUPPRESSED_ROUTE_PREFIXES = ['/sala/', '/app', '/seller'];

export function InstallBanner() {
  const { isInstallable, isInstalled, install, dismiss } = useInstallPrompt();
  const [show, setShow] = useState(false);
  // usePathname() returns the current route path on the client (and `null`
  // during SSR — same as `window.location.pathname` but Next.js-aware so
  // it works with the App Router's RSC payload). Used for the suppression
  // check below.
  const pathname = usePathname();

  useEffect(() => {
    if (!isInstallable || isInstalled) return;

    // AUDIT-070-009: do not show the banner on auth-flow routes — it
    // covers the form's primary submit button (z-50 fixed bottom-4
    // overlaps the button when the form is taller than viewport - bottom-4).
    // We check `pathname` (next/navigation) on the client; on SSR pathname
    // is `null` and the check is a no-op (the banner isn't rendered
    // server-side anyway via `if (!show || isInstalled) return null`).
    if (typeof pathname === 'string' && SUPPRESSED_ROUTES.has(pathname)) {
      setShow(false);
      return;
    }
    // Pendiente #13 (snapshot Sprint 130): also suppress on dynamic route
    // prefixes where the banner's `fixed top-4` mobile position covers
    // critical page-level buttons. Pre-fix, /sala/[slug] users joining as
    // guests couldn't click the page header "Salir" button because the PWA
    // banner (z-50) covered it — the click landed on the banner instead.
    if (typeof pathname === 'string' && SUPPRESSED_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
      setShow(false);
      return;
    }

    // SSR guard: localStorage is not available during server-side rendering
    if (typeof localStorage === 'undefined') return;

    // R2-UI-001 fix: re-check dismissed flag. The useInstallPrompt hook's
    // useEffect runs in parallel with this useEffect. If the hook's
    // useEffect has not yet checked localStorage AND returned early (which
    // would prevent setIsInstallable(true) from ever firing), the banner
    // would show even though the user dismissed it earlier in this session.
    // Double-check here to ensure the dismissed flag is respected even if
    // the hook's state has not yet caught up.
    const dismissedTs = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTs && Date.now() < parseInt(dismissedTs, 10)) {
      setShow(false);
      return;
    }

    // AUDIT-066-009 fix (SPEC-066): belt-and-suspenders `isInstallable`
    // re-check. The top-level guard at line 18 (`if (!isInstallable || isInstalled)
    // return;`) ALREADY short-circuits when isInstallable is false, but
    // this second check ensures the `setShow(true)` calls below ONLY fire
    // when isInstallable is true. This is the SPEC's prescribed "Fix
    // robusto": https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
    // only fires in browsers that meet PWA installability criteria
    // (Chrome/Edge over HTTPS with valid manifest + service worker). In
    // headless browsers (agent-browser) and some non-Chromium engines
    // (Firefox, mobile Safari) the event NEVER fires — isInstallable
    // stays false → the banner should NEVER appear in those browsers.
    // Pre-fix, the banner was showing in agent-browser's headless
    // chromium despite isInstallable being false (likely because the
    // useEffect re-ran when isInstallable briefly flipped to true after
    // a `beforeinstallprompt` event fired once, then settled back to
    // false; the visits counter had already been incremented by then).
    // The extra check here is the defense-in-depth that closes the gap.
    //   Spec ref: SPEC-066 §AUDIT-066-009, SPEC-025 §2.2 (30s delay OR
    //   2nd visit, dismiss 7 days).
    //   §6 Anti-Invención: defensive UX guard — no business logic change.
    if (!isInstallable) return;

    // Show after 30 seconds OR on 2nd visit (§2.2)
    const visits = parseInt(localStorage.getItem('pwa-visits') || '0', 10) + 1;
    localStorage.setItem('pwa-visits', visits.toString());

    if (visits >= 2) {
      setShow(true);
      return;
    }

    // fix(spec-045-A4-LOW-008): persist the 30s timer across remounts and
    // `isInstallable` transitions. Previously each effect re-run (e.g.,
    // when `beforeinstallprompt` fired and `isInstallable` flipped
    // false → true) cleared the existing `setTimeout` and restarted the
    // 30s countdown from scratch — so if the event fired every 10s the
    // banner would never appear. We now record a "show-at" timestamp in
    // localStorage on the FIRST schedule, then on subsequent effect runs
    // compute the remaining time from that stored timestamp. If the
    // timestamp is in the past, show immediately.
    const SHOW_AT_KEY = 'pwa-banner-show-at';
    const now = Date.now();
    let showAt = parseInt(localStorage.getItem(SHOW_AT_KEY) || '0', 10);
    if (!showAt || showAt < now) {
      // First schedule OR a stale timestamp from a previous session that
      // never reached the show time → (re)start the 30s countdown.
      showAt = now + 30_000;
      localStorage.setItem(SHOW_AT_KEY, showAt.toString());
    }

    const remaining = Math.max(0, showAt - now);
    const timer = setTimeout(() => {
      setShow(true);
      // Clear the stored timestamp so a future visit (when `visits`
      // hasn't yet hit 2) starts a fresh 30s countdown rather than
      // showing immediately because of an old timestamp.
      localStorage.removeItem(SHOW_AT_KEY);
    }, remaining);
    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, pathname]);

  if (!show || isInstalled) return null;

  return (
    // SPEC-177 Task H (Sprint 91): mobile top, desktop bottom.
    // Pre-fix: `fixed bottom-4` on ALL viewports. On mobile (390x844),
    // the AI Chat Widget FAB (bottom-right, 56x56) overlapped the banner
    // (full-width, 68px tall, bottom-4) — agent-browser screenshot #16
    // showed the FAB x=318,y=772 + banner x=16,y=760 → overlap TRUE.
    // Post-fix: `top-4` on mobile (no overlap with FAB which is
    // bottom-right) + `md:top-auto md:bottom-4` on desktop (FAB has
    // enough horizontal space on wide viewports — no overlap). The
    // banner still uses `max-w-lg mx-auto` so it stays centered on both
    // mobile + desktop. Decision Co-Founder QA #4.
    //
    // P-3 (SPEC-242 Track A #7 — polish batch): restructure into a
    // `pointer-events-none` wrapper + `pointer-events-auto` inner
    // banner. The wrapper spans the top/bottom strip (`left-4 right-4`)
    // but is transparent to pointer events — clicks on the empty space
    // around the visible banner (outside `max-w-lg mx-auto`) pass
    // through to underlying content. The inner banner re-enables
    // `pointer-events-auto` so the "Instalar" + "Ahora no" buttons
    // remain clickable. The smoke test found that the banner's `z-50`
    // blocked clicks on underlying content (e.g. "Ir a productos" link
    // in the seller dashboard's Quick Links grid — the banner's
    // bounding box overlapped the first grid column). With this
    // restructure, the wrapper no longer captures clicks outside the
    // visible banner, so the only blocked area is the banner itself
    // (which is intentional). Spec ref: SPEC-242 §1.8 P-3.
    <div className="fixed top-4 left-4 right-4 md:top-auto md:bottom-4 z-50 pointer-events-none">
      <div className="pointer-events-auto bg-primary text-primary-foreground p-4 rounded-lg shadow-xl flex items-center justify-between gap-4 max-w-lg mx-auto">
        <div className="flex items-center gap-3 min-w-0">
          <Download className="size-5 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-sm">Instalar Profileia</p>
            <p className="text-xs opacity-90 truncate">
              Acceso rápido desde tu pantalla de inicio
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={install}
            className="bg-primary-foreground text-primary px-4 py-2 rounded-md font-semibold text-sm hover:opacity-90 transition-opacity"
          >
            Instalar
          </button>
          <button
            onClick={() => {
              dismiss();
              setShow(false);
            }}
            className="p-1.5 rounded-md hover:bg-primary-foreground/10 transition-colors"
            aria-label="Ahora no"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
